export enum NodonHost {
  Localhost = 'localhost',
  Development = 'test.nodon.se',
  Production = 'app.nodon.se',
}

export enum NodonEnvironment {
  Localhost = 'localhost',
  Development = 'development',
  Production = 'production',
}

type APITestAudience = 'nodon-api-test';
type APIProdAudience = 'nodon-api-prod';

type LocalUIBaseUrl = `http://${NodonHost.Localhost}:50000`;
type DevelopmentUIBaseUrl = `https://${NodonHost.Development}`;
type ProductionUIBaseUrl = `https://${NodonHost.Production}`;

type Environment = `${NodonEnvironment}`;

/**
 * Environment variables available on node server
 */
export interface ServerEnvironment extends NodeJS.ProcessEnv {
  ENVIRONMENT: Environment;

  MAINTENANCE_MODE?: 'true' | 'false';

  UI_BASE_URL: LocalUIBaseUrl | DevelopmentUIBaseUrl | ProductionUIBaseUrl;

  AUTH0_ISSUER: string;
  AUTH0_DOMAIN: 'nodon.eu.auth0.com';
  AUTH0_AUDIENCE: APITestAudience | APIProdAudience;
  AUTH0_CLIENT_ID: string;
  AUTH0_CLIENT_SECRET: string;

  /**
   * Disable Auth0 authentication and authenticate every request as a static
   * test user (used by e2e tests). Only honored on localhost: the API
   * refuses to start when it is set anywhere else. db-migrate ignores leftover
   * AUTH_DISABLED in `.env` so remote-database migrations can still run.
   */
  AUTH_DISABLED?: 'true' | 'false';

  /**
   * Localhost-only Auth0 Resource Owner Password Grant. Both username and
   * password must be set. The API never enables ROPG except on a localhost
   * process (same guards as {@link AUTH_DISABLED}); it refuses to start if
   * these are set against a remote database or hosted environment.
   * Never put these on the Vite client (`VITE_*`).
   */
  AUTH0_ROPG_USERNAME?: string;
  AUTH0_ROPG_PASSWORD?: string;
  /**
   * Optional Auth0 database connection (realm). Omit when the tenant has one
   * Username-Password connection.
   */
  AUTH0_ROPG_CONNECTION?: string;
  /**
   * Optional confidential Auth0 app for ROPG. Defaults to {@link AUTH0_CLIENT_ID}
   * / {@link AUTH0_CLIENT_SECRET}. Password grant must be enabled on that app.
   */
  AUTH0_ROPG_CLIENT_ID?: string;
  AUTH0_ROPG_CLIENT_SECRET?: string;

  /**
   * API listen port. Optional; defaults to 50000.
   * Local `server/.env`, Cloud Run, and Docker all use this key — Cloud Run
   * injects it at runtime.
   */
  PORT?: string;

  /**
   * Vite UI port on localhost. Optional; defaults to 3000.
   * Separate from {@link ServerEnvironment.PORT} because that key is the API.
   */
  CLIENT_PORT?: string;

  /**
   * Database url. Either DATABASE_URL or all DB_HOST, DB_PORT, DB_USER, DB_PASSWORD, DB_DATABASE must be set.
   */
  DATABASE_URL?: string;

  /**
   * Set by `server/scripts/migrate.ts` for the db-migrate child process.
   * Marks the process as a migration so localhost-only API features
   * (AUTH_DISABLED, AUTH0_ROPG) are not applied.
   */
  NODON_MIGRATION?: 'true';

  /**
   * Set these if DATABASE_URL is not set.
   */
  DB_HOST?: string;
  DB_PORT?: string;
  DB_USER?: string;
  DB_PASSWORD?: string;
  DB_DATABASE?: string;

  /**
   * Email settings
   */
  SMTP_FROM_ADDRESS?: string;
  SMTP_TRANSPORT_URL?: string;

  /**
   * Optional override for OpenAI model selection on the server
   */
  OPEN_AI_MODEL?: string;
  /**
   * Optional JSON with per-step overrides for the project-file pipeline, e.g.
   * `{"classify":{"model":"gpt-6.1-sol","reasoningEffort":"low"},"textSegmentExtraction":{"fileDetail":"low"}}`.
   * Steps: classify, drawingDetection, drawingSegmentExtraction,
   * textSegmentExtraction, joinedBlueprintExtraction, consolidation.
   */
  PROJECT_FILE_PIPELINE_AI?: string;
  /**
   * OpenAI API key used by the server (set to 'false' to disable).
   */
  OPENAI_API_KEY?: string;

  /**
   * Gemini API key used by the server (set to 'false' to disable).
   */
  GEMINI_API_KEY?: string;

  /**
   * Anthropic (Claude) API key used by the server (set to 'false' to disable).
   */
  CLAUDE_API_KEY?: string;

  /**
   * xAI Grok API key used by the server (set to 'false' to disable).
   * {@link ServerEnvironment.GROK_API_KEY} is accepted as an alias.
   */
  XAI_API_KEY?: string;

  /**
   * Alias for {@link ServerEnvironment.XAI_API_KEY}.
   */
  GROK_API_KEY?: string;

  /**
   * Skip the live PVGIS HTTP API on a cache miss (e2e / CI). Cached rows are
   * still served. Set to `'true'` to disable.
   */
  PVGIS_DISABLED?: 'true' | 'false';

  /**
   * Optional override for Gemini model selection on the server.
   */
  GEMINI_MODEL?: string;

  /**
   * Airtable configuration
   */
  AIRTABLE_API_KEY?: string;
  AIRTABLE_BASE_ID?: string;
  AIRTABLE_TABLE_NAME?: string;

  /**
   * Browserless.io API token for server-side PDF generation
   */
  BROWSERLESS_TOKEN?: string;

  /**
   * Optional Browserless `/function` URL. Unset uses the hosted
   * `https://chrome.browserless.io/function` endpoint (same as test and prod).
   */
  BROWSERLESS_FUNCTION_URL?: string;

  /**
   * Public HTTPS origin so cloud Browserless can load the local print URL.
   * Written live by `npm run browserless:tunnel` (or set here to skip the file).
   */
  REPORT_PUBLIC_ORIGIN?: string;

  /**
   * MCP server configuration
   */
  MCP_ENABLED?: 'true' | 'false';
  MCP_REQUIRED_SCOPE?: string;

  /**
   * Auth0 public SPA client_id returned from the MCP OAuth proxy's /oauth/register
   * endpoint (RFC 7591 stub). Pre-registered in Auth0 as a Single Page Application
   * with PKCE; shared by all MCP hosts.
   */
  MCP_AUTH0_PUBLIC_CLIENT_ID?: string;

  /**
   * HMAC secret used to sign OAuth proxy state tokens that bind the MCP host's
   * redirect_uri to the Auth0 authorization code round-trip. Required when
   * MCP_ENABLED=true in production; auto-generated per-process otherwise.
   */
  MCP_STATE_SECRET?: string;

  /**
   * Optional blob driver override. Unset: `fs` when `ENVIRONMENT` is
   * `localhost`, `gcs` in development/production. Set `fs` only to force disk
   * on a hosted process (not for Cloud Run).
   */
  BLOB_STORAGE_DRIVER?: 'fs' | 'gcs';

  /**
   * Root directory for the filesystem blob driver.
   * Defaults to `{cwd}/.data/blobs` (typically `server/.data/blobs`).
   */
  BLOB_STORAGE_DIR?: string;

  /**
   * GCS bucket name. Required when the resolved driver is `gcs`.
   * Cloud Run uses Application Default Credentials (the runtime service
   * account); no JSON key file.
   */
  BLOB_STORAGE_GCS_BUCKET?: string;
}
